import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import HotelSelector from '../components/HotelSelector';
import './LandingPage.css';

const SHIPPING_ROWS = [
    { weight: '2kg', japan: '¥1,611', okinawa: '¥1,800' },
    { weight: '5kg', japan: '¥1,807', okinawa: '¥3,710' },
    { weight: '10kg', japan: '¥1,985', okinawa: '¥5,960' },
    { weight: '20kg', japan: '¥2,543', okinawa: '¥8,890' },
    { weight: '30kg', japan: '¥2,734', okinawa: '¥11,690' },
];

export default function LandingPage() {
    const { t } = useTranslation();
    const { currentUser } = useAuth();

    const steps = t('landing.steps', { returnObjects: true });
    const pricingNotes = t('landing.pricingNotes', { returnObjects: true });
    const faq = t('landing.faq', { returnObjects: true });

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    // Signed-in staff get their hotel list right under the hero; guests see it after pricing.
    const hotelSection = (
        <section className="landing-section" id="find-hotel">
            <h2>{currentUser ? t('hotelSelector.selectHotelTitle') : t('landing.findHotelTitle')}</h2>
            {!currentUser && <p className="landing-section-intro">{t('landing.findHotelText')}</p>}
            <HotelSelector embedded />
        </section>
    );

    return (
        <div className="landing">
            <section className="landing-hero">
                <h1>{t('landing.heroTitle')}</h1>
                <p>{t('landing.heroSubtitle')}</p>
                <div className="landing-hero-actions">
                    <button className="landing-cta" onClick={() => scrollTo('find-hotel')}>
                        {t('landing.ctaReport')}
                    </button>
                    <button className="landing-cta secondary" onClick={() => scrollTo('pricing')}>
                        {t('landing.ctaPricing')}
                    </button>
                </div>
            </section>

            {currentUser && hotelSection}

            <section className="landing-section">
                <h2>{t('landing.howTitle')}</h2>
                <div className="landing-steps">
                    {steps.map((step, i) => (
                        <div className="landing-step" key={i}>
                            <div className="landing-step-number">{i + 1}</div>
                            <h3>{step.title}</h3>
                            <p>{step.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="landing-section" id="pricing">
                <h2>{t('landing.pricingTitle')}</h2>
                <p className="landing-section-intro">{t('landing.pricingIntro')}</p>
                <div className="landing-table-wrap">
                    <table className="landing-pricing-table">
                        <thead>
                            <tr>
                                <th>{t('landing.tableWeight')}</th>
                                <th>{t('landing.tableJapan')}</th>
                                <th>{t('landing.tableOkinawa')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {SHIPPING_ROWS.map((row) => (
                                <tr key={row.weight}>
                                    <td>{row.weight}</td>
                                    <td>{row.japan}</td>
                                    <td>{row.okinawa}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="landing-fee-note">{t('landing.feeNote')}</p>
                <ul className="landing-pricing-notes">
                    {pricingNotes.map((note, i) => (
                        <li key={i}>{note}</li>
                    ))}
                </ul>
            </section>

            {!currentUser && hotelSection}

            <section className="landing-section">
                <h2>{t('landing.faqTitle')}</h2>
                <div className="landing-faq">
                    {faq.map((item, i) => (
                        <div className="landing-faq-item" key={i}>
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="landing-section landing-hotels">
                <h2>{t('landing.hotelsTitle')}</h2>
                <p className="landing-section-intro">{t('landing.hotelsText')}</p>
                <p className="landing-hotels-contact">
                    {t('landing.hotelsContact')}{' '}
                    <a href={`mailto:${t('company.email')}`}>{t('company.email')}</a>
                </p>
            </section>
        </div>
    );
}
